/**
 * Client-side-encrypted share links (Feature #4).
 * The scene is encrypted in the browser with a fresh AES-GCM key; only the
 * ciphertext + IV are sent to the backend. The key travels in the URL fragment
 * (`#<key>`), which browsers never send to the server — so the server can never
 * decrypt. This is the single E2E spot in v1 (CLAUDE.md §8). Reuses Excalidraw's
 * audited encryption primitives.
 */

import {
  generateEncryptionKey,
  encryptData,
  decryptData,
} from "@excalidraw/excalidraw/data/encryption";
import { serializeAsJSON } from "@excalidraw/excalidraw/data/json";

import type { ExcalidrawElement } from "@excalidraw/element/types";
import type { AppState, BinaryFiles } from "@excalidraw/excalidraw/types";

import { ryfeApi } from "./api";

const toBase64 = (buf: ArrayBuffer | Uint8Array): string => {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

const fromBase64 = (b64: string): Uint8Array<ArrayBuffer> => {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes as Uint8Array<ArrayBuffer>;
};

/**
 * Encrypt a scene and create a share link on the backend.
 * @returns the full share URL (with the decryption key in the fragment).
 */
export async function createEncryptedShare(params: {
  workspaceId: string;
  fileId: string;
  elements: readonly ExcalidrawElement[];
  appState: Partial<AppState>;
  files: BinaryFiles;
  origin?: string;
  expiresInDays?: number;
}): Promise<string> {
  const key = await generateEncryptionKey("string");
  const json = serializeAsJSON(
    params.elements,
    params.appState,
    params.files,
    "database",
  );
  const { encryptedBuffer, iv } = await encryptData(key, json);

  const { slug } = await ryfeApi.createShare(
    params.workspaceId,
    params.fileId,
    toBase64(encryptedBuffer),
    toBase64(iv),
    params.expiresInDays,
  );

  const origin =
    params.origin ??
    (typeof window !== "undefined" ? window.location.origin : "");
  return `${origin}/s/${slug}#${key}`;
}

/**
 * CC_10 Block 4 (D-57) — uniform, view-only snapshots for board / list / md.
 * Boards keep the Excalidraw-scene share above; lists and notes serialise their
 * own payload (`{ kind, name, ... }`) and go through the SAME client-side AES-GCM
 * encryption + opaque-slug backend. The key stays in the URL fragment, so the
 * server never sees plaintext — the snapshot inherits the single E2E property.
 */
export async function createEncryptedSnapshot(params: {
  workspaceId: string;
  fileId: string;
  snapshot: Record<string, unknown>;
  origin?: string;
  expiresInDays?: number;
}): Promise<string> {
  const key = await generateEncryptionKey("string");
  const { encryptedBuffer, iv } = await encryptData(
    key,
    JSON.stringify(params.snapshot),
  );
  const { slug } = await ryfeApi.createShare(
    params.workspaceId,
    params.fileId,
    toBase64(encryptedBuffer),
    toBase64(iv),
    params.expiresInDays,
  );
  const origin =
    params.origin ??
    (typeof window !== "undefined" ? window.location.origin : "");
  return `${origin}/s/${slug}#${key}`;
}

/** Fetch + decrypt a snapshot by slug + key. Returns the raw payload object. */
export async function openEncryptedSnapshot(
  slug: string,
  key: string,
): Promise<Record<string, unknown>> {
  const { ciphertext, iv } = await ryfeApi.getShare(slug);
  const decrypted = await decryptData(
    fromBase64(iv),
    fromBase64(ciphertext),
    key,
  );
  return JSON.parse(new TextDecoder().decode(decrypted));
}

/**
 * Fetch and decrypt a shared scene by slug + key.
 * @returns the parsed scene JSON (elements/appState/files).
 */
export async function openEncryptedShare(
  slug: string,
  key: string,
): Promise<{ elements: unknown; appState: unknown; files: unknown }> {
  const { ciphertext, iv } = await ryfeApi.getShare(slug);
  const decrypted = await decryptData(
    fromBase64(iv),
    fromBase64(ciphertext),
    key,
  );
  const text = new TextDecoder().decode(decrypted);
  const data = JSON.parse(text);
  return {
    elements: data.elements ?? [],
    appState: data.appState ?? {},
    files: data.files ?? {},
  };
}

/** Parse a RYFE share URL (`/s/<slug>#<key>`) into its parts. */
export function parseShareUrl(
  url: string,
): { slug: string; key: string } | null {
  try {
    const u = new URL(url);
    const m = u.pathname.match(/\/s\/([^/]+)$/);
    const key = u.hash.replace(/^#/, "");
    if (m && key) {
      return { slug: m[1], key };
    }
  } catch {
    /* invalid */
  }
  return null;
}
